import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, map } from 'rxjs';
import {
  CompleteActiveEnrollmentPromptDialogComponent,
  CompleteActiveEnrollmentPromptDialogResult,
} from 'src/app/components/main/employees/rrhh/complete-active-enrollment-prompt-dialog/complete-active-enrollment-prompt-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResource } from 'src/app/models/learning_resource.model';
import { LearningResourceAchievement } from 'src/app/models/learning_resource_achievement.model';
import { isActiveLearningResourceEnrollment, LearningResourceEnrollment } from 'src/app/models/learning_resource_enrollment.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';

export interface ActivateLearningResourceEnrollmentDialogData {
  user: User;
  learning_resource_id?: number;
}

@Component({
  selector: 'app-activate-learning-resource-enrollment-dialog',
  templateUrl: './activate-learning-resource-enrollment-dialog.component.html',
  styleUrl: './activate-learning-resource-enrollment-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatFormFieldModule, MatSelectModule],
})
export class ActivateLearningResourceEnrollmentDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<ActivateLearningResourceEnrollmentDialogComponent, boolean>);
  private readonly mat_dialog = inject(MatDialog);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly data = inject<ActivateLearningResourceEnrollmentDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly initializing = signal(true);
  readonly learning_resources = signal<LearningResource[]>([]);
  readonly active_enrollments = signal<LearningResourceEnrollment[]>([]);
  readonly completed_learning_resource_ids = signal<ReadonlySet<number>>(new Set());

  readonly user_full_name = `${this.data.user.name} ${this.data.user.surnames}`.trim();

  readonly form = this.form_builder.group({
    learning_resource_id: [null as number | null, Validators.required],
  });

  ngOnInit(): void {
    this.initDialogData();
  }

  activate(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    const learning_resource_id = this.form.value.learning_resource_id!;

    if (this.completed_learning_resource_ids().has(learning_resource_id)) {
      this.snack_bar.open('Este curso ya está registrado como completado.', undefined, { duration: 5000 });

      return;
    }

    const new_learning_resource = this.learning_resources().find(learning_resource => learning_resource.id === learning_resource_id);

    const other_active_enrollments = this.active_enrollments().filter(
      enrollment => isActiveLearningResourceEnrollment(enrollment) && enrollment.learning_resource_id !== learning_resource_id
    );

    if (other_active_enrollments.length > 0 && new_learning_resource !== undefined) {
      const prompt_ref = this.mat_dialog.open(CompleteActiveEnrollmentPromptDialogComponent, {
        width: '560px',
        minWidth: '400px',
        data: {
          active_enrollments: other_active_enrollments,
          new_learning_resource,
        },
      });

      prompt_ref
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe((result: CompleteActiveEnrollmentPromptDialogResult | undefined): void => {
          if (result === undefined) {
            return;
          }

          this.submitActivation(learning_resource_id, result);
        });

      return;
    }

    this.submitActivation(learning_resource_id, { action: 'activate_only' });
  }

  close(): void {
    if (!this.loading()) {
      this.dialog_ref.close(false);
    }
  }

  private submitActivation(learning_resource_id: number, result: CompleteActiveEnrollmentPromptDialogResult): void {
    this.loading.set(true);

    const body: {
      user_id: number;
      learning_resource_id: number;
      close_enrollment_ids?: number[];
      complete_enrollment_ids?: number[];
      complete_active_enrollment?: boolean;
    } = {
      user_id: this.data.user.id,
      learning_resource_id,
    };

    if (result.action === 'close_selected') {
      body.close_enrollment_ids = result.enrollment_ids;
    } else if (result.action === 'complete_selected') {
      body.complete_enrollment_ids = result.enrollment_ids;
    }

    this.api_service
      .activateLearningResourceEnrollment(body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => this.dialog_ref.close(true),
        error: (err: { error?: { error?: string } }): void => {
          this.loading.set(false);
          const message = err?.error?.error ?? 'Error al activar la formación.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private initDialogData(): void {
    forkJoin({
      learning_resources_response: this.api_service.getLearningResources({ order_by: 'name:asc', with: 'skills' }),
      active_enrollments: this.api_service.getActiveLearningResourceEnrollmentsForUser(this.data.user.id),
      achievements: this.api_service.getUserLearningResourceAchievements(this.data.user.id),
    })
      .pipe(
        map(({ learning_resources_response, active_enrollments, achievements }) => {
          const all_learning_resources = Array.isArray(learning_resources_response)
            ? learning_resources_response
            : learning_resources_response.data;

          return { all_learning_resources, active_enrollments, achievements };
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: ({ all_learning_resources, active_enrollments, achievements }): void => {
          const completed_learning_resource_ids = new Set(
            achievements.map((achievement: LearningResourceAchievement) => achievement.learning_resource_id)
          );
          const active_learning_resource_ids = new Set(
            active_enrollments.filter(isActiveLearningResourceEnrollment).map(enrollment => enrollment.learning_resource_id)
          );

          this.completed_learning_resource_ids.set(completed_learning_resource_ids);
          this.active_enrollments.set(active_enrollments.filter(isActiveLearningResourceEnrollment));
          this.learning_resources.set(
            all_learning_resources.filter(
              learning_resource =>
                !completed_learning_resource_ids.has(learning_resource.id) && !active_learning_resource_ids.has(learning_resource.id)
            )
          );

          if (this.data.learning_resource_id != null && completed_learning_resource_ids.has(this.data.learning_resource_id)) {
            this.snack_bar.open('Este curso ya está registrado como completado.', undefined, { duration: 5000 });
          } else if (this.data.learning_resource_id != null && active_learning_resource_ids.has(this.data.learning_resource_id)) {
            this.snack_bar.open('Este curso ya está activo para este empleado.', undefined, { duration: 5000 });
          } else if (this.data.learning_resource_id != null) {
            this.form.patchValue({ learning_resource_id: this.data.learning_resource_id });
          }

          this.initializing.set(false);
          this.change_detector_ref.markForCheck();
        },
        error: (): void => {
          this.initializing.set(false);
          this.change_detector_ref.markForCheck();
        },
      });
  }
}
